/**
 * Collision Detection Utility for LED Markers
 * 
 * This module provides functions to detect physical collisions between LED markers
 * on a print map. LEDs are 5mm square, so markers must be at least 5mm apart
 * in physical space to avoid mounting collisions.
 */

export interface LocationMarker {
  id: string;
  locationId: string;
  name: string;
  lat: number;
  long: number;
  dataProduct: 'METAR' | 'Marine' | 'Trails';
  pixelX: number;
  pixelY: number;
  manuallyAdjusted: boolean;
}

export interface PhysicalSize {
  width: number;
  height: number;
  unit: 'inches';
}

export interface Collision {
  location1Id: string;
  location2Id: string;
  distance: number; // millimeters
  minimumDistance: number; // 5mm
}

export interface CollisionResult {
  collisions: Collision[];
  collidingMarkerIds: Set<string>;
  collisionCount: number;
}

// LED physical size in millimeters
const LED_SIZE_MM = 5;

// Conversion factor: 1 inch = 25.4 millimeters
const MM_PER_INCH = 25.4;

/**
 * Calculate the physical distance between two markers in millimeters
 * 
 * @param marker1 - First location marker
 * @param marker2 - Second location marker
 * @param physicalSize - Physical dimensions of the map in inches
 * @param canvasPixelWidth - Width of the canvas in pixels (for pixel-to-physical conversion)
 * @param canvasPixelHeight - Height of the canvas in pixels (for pixel-to-physical conversion)
 * @returns Distance in millimeters
 */
function calculatePhysicalDistance(
  marker1: LocationMarker,
  marker2: LocationMarker,
  physicalSize: PhysicalSize,
  canvasPixelWidth: number,
  canvasPixelHeight: number
): number {
  // Calculate pixel distance
  const pixelDeltaX = marker2.pixelX - marker1.pixelX;
  const pixelDeltaY = marker2.pixelY - marker1.pixelY;
  const pixelDistance = Math.sqrt(pixelDeltaX * pixelDeltaX + pixelDeltaY * pixelDeltaY);
  
  // Convert physical size to millimeters
  const physicalWidthMM = physicalSize.width * MM_PER_INCH;
  const physicalHeightMM = physicalSize.height * MM_PER_INCH;
  
  // Calculate millimeters per pixel (use average of width and height for more accurate diagonal distances)
  const mmPerPixelX = physicalWidthMM / canvasPixelWidth;
  const mmPerPixelY = physicalHeightMM / canvasPixelHeight;
  
  // Calculate physical distance components
  const physicalDeltaX = pixelDeltaX * mmPerPixelX;
  const physicalDeltaY = pixelDeltaY * mmPerPixelY;
  
  // Calculate actual physical distance
  const physicalDistance = Math.sqrt(physicalDeltaX * physicalDeltaX + physicalDeltaY * physicalDeltaY);
  
  return physicalDistance;
}

/**
 * Detect collisions between LED markers
 * 
 * This function calculates the physical distance between all pairs of markers
 * and identifies collisions where markers are closer than the LED size (5mm).
 * 
 * @param locations - Array of location markers with pixel coordinates
 * @param physicalSize - Physical dimensions of the map in inches
 * @param canvasPixelWidth - Width of the canvas in pixels
 * @param canvasPixelHeight - Height of the canvas in pixels
 * @returns CollisionResult containing all collisions and colliding marker IDs
 */
export function detectCollisions(
  locations: LocationMarker[],
  physicalSize: PhysicalSize,
  canvasPixelWidth: number,
  canvasPixelHeight: number
): CollisionResult {
  const collisions: Collision[] = [];
  const collidingMarkerIds = new Set<string>();
  
  // Check all pairs of markers
  for (let i = 0; i < locations.length; i++) {
    for (let j = i + 1; j < locations.length; j++) {
      const marker1 = locations[i];
      const marker2 = locations[j];
      
      const distance = calculatePhysicalDistance(
        marker1,
        marker2,
        physicalSize,
        canvasPixelWidth,
        canvasPixelHeight
      );
      
      // Check if distance is less than LED size (collision)
      if (distance < LED_SIZE_MM) {
        collisions.push({
          location1Id: marker1.id,
          location2Id: marker2.id,
          distance,
          minimumDistance: LED_SIZE_MM,
        });
        
        collidingMarkerIds.add(marker1.id);
        collidingMarkerIds.add(marker2.id);
      }
    }
  }
  
  // Log a single summary instead of individual collisions
  if (collisions.length > 0) {
    console.log(`⚠️ Collision Detection: Found ${collisions.length} collision(s) affecting ${collidingMarkerIds.size} marker(s)`);
  }
  
  return {
    collisions,
    collidingMarkerIds,
    collisionCount: collisions.length,
  };
}

/**
 * Check if a specific marker is involved in any collision
 * 
 * @param markerId - ID of the marker to check
 * @param collisionResult - Result from detectCollisions
 * @returns true if the marker is involved in any collision
 */
export function isMarkerColliding(
  markerId: string,
  collisionResult: CollisionResult
): boolean {
  return collisionResult.collidingMarkerIds.has(markerId);
}

/**
 * Get all collisions involving a specific marker
 * 
 * @param markerId - ID of the marker
 * @param collisionResult - Result from detectCollisions
 * @returns Array of collisions involving this marker
 */
export function getMarkerCollisions(
  markerId: string,
  collisionResult: CollisionResult
): Collision[] {
  return collisionResult.collisions.filter(
    collision => collision.location1Id === markerId || collision.location2Id === markerId
  );
}
